import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import DashboardLayout from "../common/DashboardLayout";
import CertificationServices from "../../services/CertificationServices";
// Note: Grouping logic is now handled by the backend
// Frontend uses displayGroup, displayPriority, displayOrder fields from API responses
import AuthContext from "../../context/AuthContext";
import { certificateLinkFor, storedUserId } from "../../utils/certificateLink";

const statusLabels = {
  pending_review: "Pending Review",
  approved: "Approved",
  completed: "Completed",
  in_progress: "In Progress",
  not_started: "Not Started",
  expired: "Expired",
  rejected: "Rejected",
};

const normalizeCourses = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.courses)) return payload.courses;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const normalizeCertifications = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.certifications)) return payload.certifications;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatDateDisplay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const getCourseKey = (course) =>
  course?.courseKey || course?.key || course?.id || course?._id || "";

const getCourseLabel = (course) =>
  course?.title ||
  course?.name ||
  course?.courseTitle ||
  course?.displayName ||
  getCourseKey(course) ||
  "Untitled Course";

const getCertificateIdentifier = (cert = {}) =>
  cert._id || cert.id || `${cert.courseKey || "record"}-certificate`;

const getStatusBadgeClass = (status = "") => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "completed":
    case "approved":
      return "badge bg-success-subtle text-success";
    case "pending_review":
      return "badge bg-warning-subtle text-warning";
    case "rejected":
    case "expired":
      return "badge bg-danger-subtle text-danger";
    case "in_progress":
      return "badge bg-info-subtle text-info";
    default:
      return "badge bg-light text-dark";
  }
};

/**
 * Unified CertificationTracker Component
 * Used by roles 1 (School Admin), 2 (School Teacher), and 3 (Individual Teacher)
 * for tracking their own certifications
 */
const CertificationTracker = ({ 
  description = "Track completion, renewal, and upload certificates for your required SOP courses."
}) => {
  const { userrole } = useContext(AuthContext);
  const role = userrole ? Number(userrole) : null;
  
  // Determine userType based on role
  const getUserType = () => {
    if (role === 1) return "schooladmin";
    if (role === 2) return "schoolteacher";
    if (role === 3) return "teacher";
    return null;
  };
  
  const userType = getUserType();
  // Show retake button only for individual teachers (role 3)
  const showRetakeButton = role === 3;
  
  const [courses, setCourses] = useState([]);
  // Only a fallback: rows carry their own owner. See utils/certificateLink.
  const fallbackUserId = useMemo(() => storedUserId(), []);
  const [certifications, setCertifications] = useState([]);
  const [selectedCourseKey, setSelectedCourseKey] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [formState, setFormState] = useState({
    dateCompleted: "",
    renewByDate: "",
    hours: "",
    notes: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [coursesResponse, certificationsResponse] = await Promise.all([
        CertificationServices.getCourses(),
        CertificationServices.getMyCertifications(),
      ]);
      const normalizedCourses = normalizeCourses(coursesResponse);
      const normalizedCerts = normalizeCertifications(certificationsResponse);
      
      // Debug logging
      console.log('CertificationTracker: Loaded courses:', normalizedCourses.length);
      const groupsFound = new Set(normalizedCourses.map(c => c.displayGroup || 'Other').filter(Boolean));
      console.log('CertificationTracker: Groups found:', Array.from(groupsFound).sort());
      console.log('CertificationTracker: Sample course:', normalizedCourses[0]);
      
      // Log if courses don't have displayGroup
      const coursesWithoutGroup = normalizedCourses.filter(c => !c.displayGroup || c.displayGroup === 'Other Certifications');
      if (coursesWithoutGroup.length > 0) {
        console.warn('CertificationTracker: Courses without displayGroup:', coursesWithoutGroup.length);
        console.warn('Sample course keys without group:', coursesWithoutGroup.slice(0, 3).map(c => c.key || c.courseKey));
      }
      
      setCourses(normalizedCourses);
      setCertifications(normalizedCerts);

      // Set initial group/course selection using backend-provided displayGroup
      if (normalizedCourses.length > 0) {
        // Group courses by displayGroup from backend
        const grouped = {};
        normalizedCourses.forEach((course) => {
          const groupName = course.displayGroup || "Other Certifications";
          if (!grouped[groupName]) {
            grouped[groupName] = {
              name: groupName,
              priority: course.displayPriority || 999,
              courses: [],
            };
          }
          grouped[groupName].courses.push(course);
        });
        
        // Sort groups by priority and get available groups
        const availableGroups = Object.keys(grouped)
          .filter(groupName => grouped[groupName].courses.length > 0)
          .sort((a, b) => {
            const priorityA = grouped[a].priority || 999;
            const priorityB = grouped[b].priority || 999;
            return priorityA - priorityB;
          });
        
        // Set first group if none selected
        if (availableGroups.length > 0 && !selectedGroup) {
          const firstGroup = availableGroups[0];
          setSelectedGroup(firstGroup);
          
          // Set first course from first group (sorted by displayOrder)
          const firstGroupCourses = grouped[firstGroup].courses.sort((a, b) => {
            const orderA = a.displayOrder || 0;
            const orderB = b.displayOrder || 0;
            return orderA - orderB;
          });
          
          if (firstGroupCourses.length > 0) {
            const firstCourse = firstGroupCourses[0];
            const firstKey = getCourseKey(firstCourse);
            setSelectedCourseKey((prev) => prev || firstKey);
          }
        } else if (selectedGroup && grouped[selectedGroup] && grouped[selectedGroup].courses.length > 0) {
          // If group is already selected, ensure a course from that group is selected
          const currentGroupCourses = grouped[selectedGroup].courses.sort((a, b) => {
            const orderA = a.displayOrder || 0;
            const orderB = b.displayOrder || 0;
            return orderA - orderB;
          });
          const currentKey = getCourseKey(currentGroupCourses[0]);
          setSelectedCourseKey((prev) => prev || currentKey);
        } else if (normalizedCourses.length > 0) {
          // Fallback to first course if grouping fails
          const firstKey = getCourseKey(normalizedCourses[0]);
          setSelectedCourseKey((prev) => prev || firstKey);
        }
      }
    } catch (error) {
      console.error("Failed to load certification data", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      Swal.fire(
        "Error",
        `We couldn't load the certification catalog: ${error.message || "Please try again."}`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // Check and auto-assign certificates for historical completions
    const checkHistoricalCertificates = async () => {
      try {
        await CertificationServices.checkAndAssignHistoricalCertificates();
        // Refresh data after auto-assignment
        await fetchAllData();
      } catch (error) {
        console.log("Historical certificate check completed or error:", error);
      }
    };
    checkHistoricalCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((course) => getCourseKey(course) === selectedCourseKey),
    [courses, selectedCourseKey]
  );

  const selectedCertification = useMemo(
    () =>
      certifications.find(
        (cert) => cert?.courseKey === selectedCourseKey
      ) || null,
    [certifications, selectedCourseKey]
  );

  useEffect(() => {
    if (!selectedCourseKey && courses.length > 0) {
      setSelectedCourseKey(getCourseKey(courses[0]));
    }
  }, [courses, selectedCourseKey]);

  useEffect(() => {
    if (selectedCertification) {
      setFormState({
        dateCompleted: toDateInputValue(selectedCertification?.dateCompleted),
        renewByDate: toDateInputValue(selectedCertification?.renewByDate),
        hours:
          selectedCertification?.hours !== undefined &&
          selectedCertification?.hours !== null
            ? String(selectedCertification.hours)
            : "",
        notes: selectedCertification?.notes || "",
      });
    } else {
      setFormState({
        dateCompleted: "",
        renewByDate: "",
        hours: "",
        notes: "",
      });
    }
  }, [selectedCertification]);

  const handleCourseChange = (event) => {
    const newCourseKey = event.target.value;
    setSelectedCourseKey(newCourseKey);
    
    // Update selected group if the course belongs to a different group (using backend displayGroup)
    const selectedCourse = courses.find(c => getCourseKey(c) === newCourseKey);
    if (selectedCourse && selectedCourse.displayGroup && selectedCourse.displayGroup !== selectedGroup) {
      setSelectedGroup(selectedCourse.displayGroup);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDownloadCertificate = async (cert, fallbackCourseName) => {
    if (!cert?.certificateUrl) {
      Swal.fire(
        "No certificate",
        "There is no certificate file saved for this course yet.",
        "info"
      );
      return;
    }

    const identifier = getCertificateIdentifier(cert);
    setDownloadingId(identifier);
    try {
      const safeCourseLabel = (fallbackCourseName || "Certificate").replace(
        /[\\/:*?"<>|]+/g,
        "_"
      );
      const { blob, filename, contentType } =
        await CertificationServices.downloadCertificate({
          certId:
            cert._id || cert.id || cert.certificationId || cert.courseKey,
          fallbackName: `${safeCourseLabel}.pdf`,
        });
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `${safeCourseLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download certificate", error);
      Swal.fire(
        "Download failed",
        "We couldn't download the certificate. Please try again.",
        "error"
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedCourseKey) {
      Swal.fire(
        "Course Required",
        "Please choose a course before saving.",
        "warning"
      );
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        courseKey: selectedCourseKey,
        dateCompleted: formState.dateCompleted || undefined,
        renewByDate: formState.renewByDate || undefined,
        hours: formState.hours ? Number(formState.hours) : undefined,
        notes: formState.notes || undefined,
      };

      await CertificationServices.upsertCertification(payload);
      await fetchAllData();
      Swal.fire("Saved", "Certification details were updated.", "success");
    } catch (error) {
      console.error("Failed to save certification", error);
      Swal.fire(
        "Error",
        "We could not save the certification. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const validateFile = (file) => {
    if (!file) {
      Swal.fire("No file selected", "Please choose a file to upload.", "info");
      return false;
    }
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire(
        "Invalid file",
        "Certificates must be a PDF, JPG, or PNG file.",
        "warning"
      );
      return false;
    }
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      Swal.fire(
        "File too large",
        "Certificates must be 5 MB or smaller.",
        "warning"
      );
      return false;
    }
    return true;
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !validateFile(file)) {
      event.target.value = "";
      return;
    }

    if (!selectedCertification?._id) {
      Swal.fire(
        "Save Required",
        "Please save the certification details before uploading a document.",
        "info"
      );
      event.target.value = "";
      return;
    }

    setIsUploading(true);
    try {
      await CertificationServices.uploadCertificate(
        selectedCertification._id,
        file
      );
      await fetchAllData();
      Swal.fire(
        "Uploaded",
        "Your certificate was uploaded and sent for review.",
        "success"
      );
    } catch (error) {
      console.error("Failed to upload certificate", error);
      Swal.fire(
        "Upload failed",
        "We couldn't upload the file. Please try again.",
        "error"
      );
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleDeleteCertificate = async () => {
    if (!selectedCertification?._id || !selectedCertification?.certificateUrl) {
      Swal.fire(
        "No certificate",
        "There is no certificate on file for this course.",
        "info"
      );
      return;
    }
    const confirmation = await Swal.fire({
      title: "Remove certificate?",
      text: "This will delete the uploaded certificate for this course.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it",
    });
    if (!confirmation.isConfirmed) return;

    setIsDeleting(true);
    try {
      await CertificationServices.deleteCertificate(selectedCertification._id);
      await fetchAllData();
      Swal.fire(
        "Removed",
        "The certificate has been removed from this course.",
        "success"
      );
    } catch (error) {
      console.error("Failed to delete certificate", error);
      Swal.fire(
        "Error",
        "We could not remove the certificate. Please try again.",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetCertification = async () => {
    if (!selectedCertification?.courseKey) {
      Swal.fire(
        "No record",
        "You have not saved any progress for this course yet.",
        "info"
      );
      return;
    }
    const confirmation = await Swal.fire({
      title: "Reset certification?",
      text: "All saved details for this course will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset it",
    });
    if (!confirmation.isConfirmed) return;

    setIsDeleting(true);
    try {
      await CertificationServices.deleteCertification(
        selectedCertification.courseKey
      );
      await fetchAllData();
      Swal.fire("Reset", "The certification record has been cleared.", "success");
    } catch (error) {
      console.error("Failed to reset certification", error);
      Swal.fire(
        "Error",
        "We could not reset the certification. Please try again.",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const triggerFileSelect = () => {
    if (!selectedCourseKey) {
      Swal.fire(
        "Course Required",
        "Please choose and save a course before uploading.",
        "info"
      );
      return;
    }
    if (!selectedCertification?._id) {
      Swal.fire(
        "Save required",
        "Please save the certification before uploading a file.",
        "info"
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const resolvedStatus =
    statusLabels[selectedCertification?.status] ||
    selectedCertification?.status ||
    "Not Started";

  // Group certifications using backend-provided displayGroup, displayPriority, displayOrder
  const groupedCertifications = useMemo(() => {
    const grouped = {};
    certifications.forEach((cert) => {
      const groupName = cert.displayGroup || "Other Certifications";
      if (!grouped[groupName]) {
        grouped[groupName] = {
          name: groupName,
          priority: cert.displayPriority || 999,
          certifications: [],
        };
      }
      grouped[groupName].certifications.push(cert);
    });
    
    // Sort certifications within each group by displayOrder
    Object.keys(grouped).forEach((groupName) => {
      grouped[groupName].certifications.sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });
    });
    
    return grouped;
  }, [certifications]);

  const sortedGroupNames = useMemo(() => {
    const groupNames = Object.keys(groupedCertifications);
    return groupNames.sort((a, b) => {
      const priorityA = groupedCertifications[a].priority || 999;
      const priorityB = groupedCertifications[b].priority || 999;
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.localeCompare(b);
    });
  }, [groupedCertifications]);

  // Group courses using backend-provided displayGroup, displayPriority, displayOrder
  const groupedCourses = useMemo(() => {
    const grouped = {};
    courses.forEach((course) => {
      const groupName = course.displayGroup || "Other Certifications";
      if (!grouped[groupName]) {
        grouped[groupName] = {
          name: groupName,
          priority: course.displayPriority || 999,
          courses: [],
        };
      }
      grouped[groupName].courses.push(course);
    });
    
    // Sort courses within each group by displayOrder
    Object.keys(grouped).forEach((groupName) => {
      grouped[groupName].courses.sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });
    });
    
    return grouped;
  }, [courses]);

  // Get available groups (only those with courses) sorted by priority
  const availableGroups = useMemo(() => {
    if (!courses || courses.length === 0) {
      return [];
    }
    const groupNames = Object.keys(groupedCourses);
    const filtered = groupNames
      .filter(groupName => groupedCourses[groupName] && groupedCourses[groupName].courses.length > 0)
      .sort((a, b) => {
        const priorityA = groupedCourses[a].priority || 999;
        const priorityB = groupedCourses[b].priority || 999;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        return a.localeCompare(b);
      });
    
    // Debug logging
    if (filtered.length === 0 && courses.length > 0) {
      console.warn('CertificationTracker: No groups found but courses exist:', {
        coursesCount: courses.length,
        sampleCourse: courses[0],
        hasDisplayGroup: courses[0]?.displayGroup,
        courseKeys: courses.slice(0, 5).map(c => c.key || c.courseKey)
      });
    }
    
    return filtered;
  }, [groupedCourses, courses]);

  // Get courses filtered by selected group
  const filteredCourses = useMemo(() => {
    if (!selectedGroup || !groupedCourses[selectedGroup]) {
      return courses; // Fallback to all courses if no group selected
    }
    return groupedCourses[selectedGroup].courses;
  }, [selectedGroup, groupedCourses, courses]);

  const handleGroupChange = (event) => {
    const newGroup = event.target.value;
    setSelectedGroup(newGroup);
    
    // Auto-select first course in the new group
    if (groupedCourses[newGroup] && groupedCourses[newGroup].courses.length > 0) {
      const firstCourse = groupedCourses[newGroup].courses[0];
      const firstKey = getCourseKey(firstCourse);
      setSelectedCourseKey(firstKey);
    }
  };

  return (
    <DashboardLayout 
      role={role} 
      userType={userType}
      activeMenu="Certifications"
      layoutType="topnav"
    >
      {isLoading ? (
        <div className="card">
          <div className="card-body text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* My certification overview - moved to top */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">My certification overview</h5>
              {certifications.length === 0 ? (
                <p className="text-muted mb-0">
                  You have not saved any certification progress yet. Select
                  a course below to get started.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>Course</th>
                        <th>Status</th>
                        <th>Completed</th>
                        <th>Renew by</th>
                        <th>Hours</th>
                        <th>Certificate</th>
                        {showRetakeButton && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGroupNames.map((groupName) => {
                        const group = groupedCertifications[groupName];
                        if (!group || group.certifications.length === 0) return null;

                        return (
                          <React.Fragment key={groupName}>
                            {/* Group Header Row */}
                            <tr className="table-group-header" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                              <td colSpan={showRetakeButton ? 7 : 6} style={{ padding: '12px 16px', fontSize: '1.1rem', borderTop: '2px solid #dee2e6' }}>
                                {groupName}
                              </td>
                            </tr>
                            {/* Group Certifications */}
                            {group.certifications.map((cert) => {
                              const course = courses.find(
                                (item) => getCourseKey(item) === cert.courseKey
                              );
                              const label =
                                getCourseLabel(course) || cert.courseKey;
                              const statusLabel =
                                statusLabels[cert.status] ||
                                cert.status ||
                                "Not Started";
                              const isCompleted = ["completed", "approved"].includes(
                                (cert.status || "").toLowerCase()
                              );
                              const user = JSON.parse(localStorage.getItem("user") || "{}");
                              const teacherId = user?._id || user?.id || "";
                              const retakePath = teacherId
                                ? `/teachertest/mcq-test?teacher_id=${encodeURIComponent(teacherId)}`
                                : "/teachertest/mcq-test";

                              return (
                                <tr key={`${cert.courseKey}-${cert._id || "record"}`}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {isCompleted && (
                                        <i
                                          className="bx bx-check-circle text-success me-2"
                                          style={{ fontSize: "1.2rem" }}
                                          title="Certified"
                                        />
                                      )}
                                      {label}
                                    </div>
                                  </td>
                                  <td>
                                    <span className={getStatusBadgeClass(cert.status)}>
                                      {statusLabel}
                                    </span>
                                  </td>
                                  <td>{formatDateDisplay(cert.dateCompleted)}</td>
                                  <td>{formatDateDisplay(cert.renewByDate)}</td>
                                  <td>
                                    {cert.hours !== undefined &&
                                    cert.hours !== null
                                      ? cert.hours
                                      : "—"}
                                  </td>
                                  <td>
                                    {/*
                                      A stored certificate file if there is one,
                                      otherwise the certificate rendered on
                                      demand from the record. This offered only
                                      the first, and certificateUrl is null on
                                      every completed certification in the
                                      platform — so this column showed an em
                                      dash for everybody.
                                    */}
                                    {cert.certificateUrl ? (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={() =>
                                          handleDownloadCertificate(cert, label)
                                        }
                                        disabled={
                                          downloadingId ===
                                          getCertificateIdentifier(cert)
                                        }
                                      >
                                        {downloadingId ===
                                        getCertificateIdentifier(cert)
                                          ? "Downloading..."
                                          : "View"}
                                      </button>
                                    ) : certificateLinkFor(cert, fallbackUserId) ? (
                                      <Link
                                        to={certificateLinkFor(cert, fallbackUserId)}
                                        className="btn btn-link btn-sm p-0"
                                      >
                                        View
                                      </Link>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  {showRetakeButton && (
                                    <td>
                                      <Link
                                        to={retakePath}
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        <i className="bx bx-refresh me-1"></i>
                                        Retake Course
                                      </Link>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div>
                  <h5 className="mb-1">Certification Tracker</h5>
                  <p className="text-muted mb-0">
                    {description}
                  </p>
                </div>
                {selectedCertification?.status && (
                  <span className="badge bg-primary-subtle text-primary fw-semibold fs-6 mt-3 mt-md-0">
                    Status: {resolvedStatus}
                  </span>
                )}
              </div>
              <form className="mt-4" onSubmit={handleSave}>
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Select category</label>
                    <select
                      className="form-control mb-3"
                      value={selectedGroup}
                      onChange={handleGroupChange}
                      disabled={isLoading || availableGroups.length === 0}
                    >
                      {availableGroups.length === 0 ? (
                        <option value="">{isLoading ? "Loading categories..." : "No categories available"}</option>
                      ) : (
                        availableGroups.map((groupName) => (
                        <option key={groupName} value={groupName}>
                          {groupName}
                        </option>
                        ))
                      )}
                    </select>
                    {!isLoading && availableGroups.length === 0 && courses.length === 0 && (
                      <small className="text-danger d-block mt-1">
                        No courses found. Please check your connection or contact support.
                      </small>
                    )}
                    {!isLoading && availableGroups.length === 0 && courses.length > 0 && (
                      <small className="text-warning d-block mt-1">
                        Warning: Courses loaded but no categories found. Check backend displayGroup configuration.
                      </small>
                    )}
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Select course</label>
                    <select
                      className="form-control"
                      value={selectedCourseKey}
                      onChange={handleCourseChange}
                    >
                      {filteredCourses.map((course) => {
                        const key = getCourseKey(course);
                        return (
                          <option key={key} value={key}>
                            {getCourseLabel(course)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {selectedCourse?.description && (
                    <div className="col-12">
                      <div className="alert alert-info mb-0">
                        {selectedCourse.description}
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label">Date completed</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dateCompleted"
                      value={formState.dateCompleted}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Renew by date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="renewByDate"
                      value={formState.renewByDate}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Hours spent</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      className="form-control"
                      name="hours"
                      value={formState.hours}
                      onChange={handleFormChange}
                      placeholder="e.g. 1.5"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      {role === 2 ? "Notes" : "Notes for reviewers"}
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      name="notes"
                      value={formState.notes}
                      onChange={handleFormChange}
                      placeholder={
                        role === 2
                          ? "Optional notes for your administrator"
                          : role === 1
                          ? "Add context for school administrators (optional)"
                          : "Add context for school administrators (optional)"
                      }
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 mt-4 flex-wrap">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save certification"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleResetCertification}
                    disabled={
                      isDeleting || !selectedCertification?.courseKey
                    }
                  >
                    {isDeleting ? "Working..." : "Reset record"}
                  </button>
                </div>
              </form>
              <hr className="my-4" />
              <div>
                <h6 className="mb-3">Certificate upload</h6>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="d-none"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleUpload}
                />
                {selectedCertification?.certificateUrl ? (
                  <div className="alert alert-success d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                    <div className="me-3">
                      <strong>Certificate on file.</strong>{" "}
                      <span className="d-block d-md-inline">
                        Download it to review or replace with a new upload.
                      </span>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          handleDownloadCertificate(
                            selectedCertification,
                            getCourseLabel(selectedCourse)
                          )
                        }
                        disabled={
                          downloadingId ===
                          getCertificateIdentifier(selectedCertification)
                        }
                      >
                        {downloadingId ===
                        getCertificateIdentifier(selectedCertification)
                          ? "Downloading..."
                          : "Download"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={handleDeleteCertificate}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info">
                    No certificate uploaded yet. Upload a PDF, JPG, or PNG
                    file (max 5&nbsp;MB) once you have completed the course.
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={triggerFileSelect}
                  disabled={isUploading || isSaving}
                >
                  {isUploading ? "Uploading..." : "Upload certificate"}
                </button>
              </div>
            </div>
          </div>
          
          {/* Show all available courses organized by group */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">All Available Courses ({courses.length} total)</h5>
              <div className="accordion" id="coursesAccordion">
                {availableGroups.map((groupName, groupIndex) => {
                  const group = groupedCourses[groupName];
                  if (!group || !group.courses || group.courses.length === 0) return null;
                  
                  return (
                    <div key={groupName} className="accordion-item">
                      <h2 className="accordion-header" id={`heading${groupIndex}`}>
                        <button
                          className={`accordion-button ${groupIndex === 0 ? '' : 'collapsed'}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#collapse${groupIndex}`}
                          aria-expanded={groupIndex === 0 ? 'true' : 'false'}
                          aria-controls={`collapse${groupIndex}`}
                        >
                          <strong>{groupName}</strong>
                          <span className="badge bg-secondary ms-2">{group.courses.length} courses</span>
                        </button>
                      </h2>
                      <div
                        id={`collapse${groupIndex}`}
                        className={`accordion-collapse collapse ${groupIndex === 0 ? 'show' : ''}`}
                        aria-labelledby={`heading${groupIndex}`}
                        data-bs-parent="#coursesAccordion"
                      >
                        <div className="accordion-body">
                          <ul className="list-unstyled mb-0">
                            {group.courses.sort((a, b) => {
                              const orderA = a.displayOrder || 0;
                              const orderB = b.displayOrder || 0;
                              return orderA - orderB;
                            }).map((course) => {
                              const courseKey = getCourseKey(course);
                              const cert = certifications.find(c => c.courseKey === courseKey);
                              const isStarted = !!cert;
                              
                              return (
                                <li key={courseKey} className="mb-2 d-flex align-items-center">
                                  <span className={isStarted ? 'text-success' : 'text-muted'}>
                                    {isStarted && <span className="badge bg-success me-2">Started</span>}
                                    {getCourseLabel(course)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

CertificationTracker.propTypes = {
  description: PropTypes.string,
};

export default CertificationTracker;

